import { Box, Column, Row } from '@shipt/design-system-layouts'
import { ProductDetailStars } from '@/features/shop/components/ProductDetailRatingsAndReviews/ProductDetailStars'
import { Body } from '@shipt/design-system-typography'
import {
  CloseCircleFillIcon,
  InfoCircleIcon,
  PositiveCircleFillIcon,
} from '@shipt/design-system-icons'
import { type Review } from '@/features/account/services/RatingsAndReviews/types'
import { formatDate } from '@/features/shared/utils/date'
import { IconButton, TextButton } from '@shipt/design-system-buttons'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicRatingsAndReviewsInfoModal } from '@/features/shop/components/Modal/RatingsAndReviewsInfoModal/DynamicRatingsAndReviewsInfoModal'
import styled from 'styled-components'
import { useState } from 'react'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'

const RecommendationStatus = ({ isRecommended }: { isRecommended: boolean | null }) => {
  if (isRecommended === null) return null

  return (
    <Row
      spacing="sm"
      align="center"
      role="group"
      aria-label={`Customer recommendation: ${
        isRecommended ? 'Would recommend' : 'Would not recommend'
      }`}
    >
      {isRecommended ? (
        <>
          <PositiveCircleFillIcon
            size="md"
            feedbackType="positive"
            concept="feedback"
            surface="inverse"
          />
          <Body variant="secondary" size="md">
            Would recommend
          </Body>
        </>
      ) : (
        <>
          <CloseCircleFillIcon size="md" />
          <Body variant="secondary" size="md">
            Would not recommend
          </Body>
        </>
      )}
    </Row>
  )
}

export const ProductDetailCustomerReview = ({ review }: { review: Review }) => {
  const {
    author,
    content,
    is_incentivized: isIncentivized,
    is_recommended,
    rating,
    review_source,
    submitted_at,
    title,
  } = review
  const { openDialog } = useHandleDialog()
  const { screenSm } = useMediaQuery()
  const desktopCharLimit = 575
  const mobileCharLimit = 275

  const charLimit = screenSm ? mobileCharLimit : desktopCharLimit

  const shouldTruncate = Boolean(content && content.length > charLimit)
  const [isClamped, setIsClamped] = useState(shouldTruncate)

  const truncatedContent = isClamped ? content?.slice(0, charLimit) + '... ' : content

  return (
    <Box paddingTop="lg">
      <Row spacing="xs" paddingBottom="xs" align="center">
        <ProductDetailStars rating={rating} />
        {!!author?.display_name && (
          <Body variant="secondary" size="sm">
            {author.display_name}
          </Body>
        )}
      </Row>
      <Column spacing="sm">
        <Body strong>{title}</Body>
        <RecommendationStatus isRecommended={is_recommended} />
        <Body size="md">
          {truncatedContent}
          {!!isClamped && (
            <SeeMoreButton
              size="md"
              onClick={() => setIsClamped(false)}
              aria-label="See more reviews"
            >
              See more
            </SeeMoreButton>
          )}
        </Body>
        <Row align="center">
          {!!isIncentivized && (
            <>
              <Body
                strong
                variant="secondary"
                size="sm"
                aria-label="This reviewer received an incentive"
              >
                Incentivized
              </Body>
              <InfoButton
                size="sm"
                variant="ghost"
                type="button"
                icon={InfoCircleIcon}
                onClick={() => {
                  trackElementClicked({
                    display_sublocation: 'customer_review',
                    display_format: 'ratings_and_reviews',
                    content: 'info',
                    type: 'button',
                  })
                  openDialog(DynamicRatingsAndReviewsInfoModal)
                }}
                aria-label="Learn more about incentivized reviews"
              />
              <Body variant="secondary" size="sm">
                {'\u00A0•\u00A0'}
              </Body>
            </>
          )}
          <Body
            size="sm"
            variant="secondary"
            aria-label={`Review submitted on ${formatDate(
              submitted_at,
              'MMMM do, yyyy',
            )} from ${review_source.name}`}
          >
            {formatDate(submitted_at, 'MM/dd/yyyy')} • Posted on {review_source.name}
          </Body>
        </Row>
      </Column>
    </Box>
  )
}

const InfoButton = styled(IconButton)`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.color.content.base.neutral.secondary};
`

const SeeMoreButton = styled(TextButton)`
  text-decoration: underline;
  display: inline;
`
